import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Container,
  Stack,
  Fade,
} from "@mui/material";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import NavBar from "../../../Components/NavBar";
import { colors } from "../../../../theme";
import { CommonBreadcrumbs } from "../../../Components/ui";
import { Path } from "../../../../static/Constants";

import {
  HelpHero,
  HelpVideoGuides,
  HELP_VIDEOS,
  HelpFaqSection,
  FAQ_DATA,
  HelpContactCard,
  HelpEmptyState,
  HelpHeroSkeleton,
  HelpVideoSkeleton,
} from "./components";

import "./HelpStyles.css";

const Help = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // Initialize and complete loading with safeguard timeout (never stuck on loader)
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 200);

    return () => clearTimeout(timer);
  }, []);

  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery("");
  }, []);

  const handleScrollToSection = useCallback((sectionKey) => {
    let targetId = "";
    if (sectionKey === "videos") targetId = "video-tutorials-section";
    else if (sectionKey === "faqs") targetId = "faq-section";
    else if (sectionKey === "contact") targetId = "contact-section";

    if (targetId) {
      const el = document.getElementById(targetId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, []);

  // Filter calculations to determine global empty state
  const totalMatchingVideos = useMemo(() => {
    if (!searchQuery.trim()) return HELP_VIDEOS.length;
    const q = searchQuery.toLowerCase().trim();
    return HELP_VIDEOS.filter(
      (v) =>
        v.title.toLowerCase().includes(q) ||
        v.description.toLowerCase().includes(q) ||
        v.badge.toLowerCase().includes(q)
    ).length;
  }, [searchQuery]);

  const totalMatchingFaqs = useMemo(() => {
    if (!searchQuery.trim()) return FAQ_DATA.length;
    const q = searchQuery.toLowerCase().trim();
    return FAQ_DATA.filter((f) => {
      const questionMatch = f.question.toLowerCase().includes(q);
      const solutionMatch =
        typeof f.solution === "string"
          ? f.solution.toLowerCase().includes(q)
          : Array.isArray(f.solution) &&
            f.solution.some(
              (item) =>
                (item.subTitle && item.subTitle.toLowerCase().includes(q)) ||
                (item.subSolution && item.subSolution.toLowerCase().includes(q))
            );
      return questionMatch || solutionMatch;
    }).length;
  }, [searchQuery]);

  const hasNoResults =
    Boolean(searchQuery.trim()) &&
    totalMatchingVideos === 0 &&
    totalMatchingFaqs === 0;

  return (
    <Box
      id="helpScreen"
      sx={{
        bgcolor: colors.dashboard.canvas,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <NavBar />
      <ToastContainer />

      <Container
        maxWidth="lg"
        sx={{
          pt: { xs: "96px", sm: "108px", md: "116px" },
          pb: 8,
          flexGrow: 1,
        }}
      >
        {/* Breadcrumb Navigation */}
        <CommonBreadcrumbs
          items={[
            { label: "Home", path: Path.root },
            { label: "Help & Knowledge Base" },
          ]}
        />

        {isLoading ? (
          <Stack spacing={4}>
            <HelpHeroSkeleton />
            <HelpVideoSkeleton count={6} />
          </Stack>
        ) : (
          <Fade in timeout={300}>
            <Stack spacing={4}>
              {/* Hero Banner with Search & Navigation */}
              <HelpHero
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
                videoCount={HELP_VIDEOS.length}
                faqCount={FAQ_DATA.length}
                onScrollToSection={handleScrollToSection}
              />

              {/* Global Empty State if search finds nothing across videos & FAQs */}
              {hasNoResults ? (
                <HelpEmptyState
                  title="No Knowledge Base Results Found"
                  description={`We couldn't find any video tutorials or FAQs matching "${searchQuery}". Please try another keyword or browse all topics below.`}
                  actionText="Reset Search Filter"
                  onAction={handleClearSearch}
                />
              ) : (
                <>
                  {/* Step-by-Step Video Tutorials */}
                  <HelpVideoGuides
                    searchQuery={searchQuery}
                    onClearSearch={handleClearSearch}
                  />

                  {/* Frequently Asked Questions */}
                  <HelpFaqSection
                    searchQuery={searchQuery}
                    onClearSearch={handleClearSearch}
                  />
                </>
              )}

              {/* Support Channels & Contact Info */}
              <HelpContactCard />
            </Stack>
          </Fade>
        )}
      </Container>
    </Box>
  );
};

export default Help;
